import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SupportService } from '@/app/services/support/support.service';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import {
  SupportAdminOption,
  SupportAlertPreferences,
  SupportAuthorType,
  SupportAutoAssignByCategory,
  SupportStatsWindow,
  SupportTicketDetail,
  SupportTicketDirection,
  SupportTicketRegion,
  SupportTicketStats,
  SupportTicketStatus,
  SupportTicketSummary,
  SupportUserSummary,
  SupportUserType,
  SupportTemplateMessage,
} from '@/app/common/general-types/support-ticket';
import {
  SUPPORT_TICKET_CATEGORY_OPTIONS,
  supportTicketCategoryLabel,
} from '@/app/common/support-ticket-category.util';
import {
  SUPPORT_REGION_FILTER_OPTIONS,
  supportCountryFlagForTicket,
} from '@/app/common/support-region.util';
import {
  formatSupportAdminDateTime,
  formatSupportDurationMs,
} from '@/app/common/support-date.util';
import { SUPPORT_ADMIN_ROLES } from '@/app/common/support-admin-roles.util';
import {
  createSupportTemplateMessage,
  insertSupportTemplateToken,
  SUPPORT_FIRSTNAME_TOKEN,
  supportTemplatePreviewWithSampleName,
} from '@/app/common/support-template-message.util';
import { DialogComponent } from '@/app/atoms/dialog/dialog.component';
import { SupportOutboundTicket } from './molecules/support-outbound-ticket/support-outbound-ticket';

@Component({
  selector: 'app-support-admin',
  imports: [FormsModule, DialogComponent, SupportOutboundTicket],
  templateUrl: './support-admin.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportAdminComponent implements OnInit, OnDestroy {
  private readonly supportService = inject(SupportService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  subscriptionList = new Subscription();

  readonly canAccessSupportAdmin = signal(false);
  readonly loading = signal(true);
  readonly statsLoading = signal(true);
  readonly tickets = signal<SupportTicketSummary[]>([]);
  readonly totalTickets = signal(0);
  readonly pageIndex = signal(0);
  readonly pageSize = 25;
  readonly stats = signal<SupportTicketStats | null>(null);
  readonly showSettings = signal(false);
  readonly settingsLoading = signal(false);
  readonly settingsSaving = signal(false);
  readonly settingsError = signal('');
  readonly showTemplates = signal(false);
  readonly templatesLoading = signal(false);
  readonly templatesSaving = signal(false);
  readonly templatesError = signal('');
  readonly showOutbound = signal(false);

  statsWindow: SupportStatsWindow = '30d';
  statusFilter: SupportTicketStatus | 'all' = 'open';
  /** `all` | `mine` | `unassigned` | a specific admin userId */
  assignedFilter = 'all';
  /** Set by clicking the Inbound / Outbound stat tiles; null shows both. */
  readonly directionFilter = signal<SupportTicketDirection | null>(null);
  regionFilter: SupportTicketRegion | 'all' = 'all';
  search = '';
  admins: SupportAdminOption[] = [];
  autoAssignByCategory: SupportAutoAssignByCategory = this.createEmptyAutoAssignByCategory();
  autoCloseEnabled = true;
  autoCloseAfterDays = 7;
  notifyUserOnAdminClose = true;
  notifyUserOnAutoClose = true;
  adminReplyNotifyDelayMinutes = 2;
  skipDelayForFirstResponse = true;
  templateMessages: SupportTemplateMessage[] = [];

  private templatesLoadedOnce = false;

  readonly statsWindowOptions: { value: SupportStatsWindow; label: string }[] = [
    { value: '7d', label: '7d' },
    { value: '30d', label: '30d' },
    { value: 'all', label: 'All' },
  ];
  readonly categoryLabel = supportTicketCategoryLabel;
  readonly countryFlagForTicket = supportCountryFlagForTicket;
  readonly regionFilterOptions = SUPPORT_REGION_FILTER_OPTIONS;
  readonly formatDateTime = formatSupportAdminDateTime;
  readonly formatDuration = formatSupportDurationMs;
  readonly categoryOptions = SUPPORT_TICKET_CATEGORY_OPTIONS;
  readonly skeletonRows = [0, 1, 2, 3, 4, 5];
  readonly settingsSkeletonRows = [0, 1, 2, 3];
  readonly templatesSkeletonRows = [0, 1];
  readonly previewTemplateBody = supportTemplatePreviewWithSampleName;

  ngOnInit(): void {
    const allowed = this.currentUserService.hasAnyRole(SUPPORT_ADMIN_ROLES);
    this.canAccessSupportAdmin.set(allowed);
    if (!allowed) {
      this.router.navigate(['/']);
      return;
    }

    this.supportService.refreshAssignedOpenTicketCount();
    this.loadAdmins();
    this.loadTickets();
    this.loadStats();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  loadAdmins(): void {
    this.subscriptionList.add(
      this.supportService.listAssignableAdmins().subscribe({
        next: (admins) => {
          this.admins = admins;
          this.cdr.markForCheck();
        },
        error: () => {
          this.admins = [];
          this.cdr.markForCheck();
        },
      }),
    );
  }

  loadTickets(resetPage = false): void {
    if (resetPage) {
      this.pageIndex.set(0);
    }
    this.loading.set(true);
    const specificAdminId = this.specificAssignedAdminId();
    const skip = this.pageIndex() * this.pageSize;
    this.subscriptionList.add(
      this.supportService
        .listAdminTickets({
          status: this.statusFilter,
          assigned: specificAdminId ? 'all' : (this.assignedFilter as 'all' | 'mine' | 'unassigned'),
          assignedAdminUserId: specificAdminId,
          direction: this.directionFilter() ?? undefined,
          region: this.regionFilter === 'all' ? undefined : this.regionFilter,
          search: this.search.trim() || undefined,
          skip,
          take: this.pageSize,
        })
        .subscribe({
          next: (response) => {
            this.tickets.set(response.data ?? []);
            this.totalTickets.set(response.total ?? 0);
            const maxPage = Math.max(0, Math.ceil((response.total ?? 0) / this.pageSize) - 1);
            if (this.pageIndex() > maxPage) {
              this.pageIndex.set(maxPage);
              if (response.total > 0) {
                this.loadTickets();
                return;
              }
            }
            this.loading.set(false);
            this.cdr.markForCheck();
          },
          error: () => {
            this.tickets.set([]);
            this.totalTickets.set(0);
            this.loading.set(false);
            this.cdr.markForCheck();
          },
        }),
    );
  }

  onFiltersChange(): void {
    this.loadTickets(true);
  }

  goToPreviousPage(): void {
    if (this.pageIndex() <= 0) {
      return;
    }
    this.pageIndex.update((page) => page - 1);
    this.loadTickets();
  }

  goToNextPage(): void {
    if (!this.canGoNext()) {
      return;
    }
    this.pageIndex.update((page) => page + 1);
    this.loadTickets();
  }

  canGoPrevious(): boolean {
    return this.pageIndex() > 0;
  }

  canGoNext(): boolean {
    return (this.pageIndex() + 1) * this.pageSize < this.totalTickets();
  }

  pageRangeLabel(): string {
    const total = this.totalTickets();
    if (total === 0) {
      return '0 of 0';
    }
    const start = this.pageIndex() * this.pageSize + 1;
    const end = Math.min(total, (this.pageIndex() + 1) * this.pageSize);
    return `${start}–${end} of ${total}`;
  }

  private specificAssignedAdminId(): string | undefined {
    if (
      this.assignedFilter === 'all' ||
      this.assignedFilter === 'mine' ||
      this.assignedFilter === 'unassigned'
    ) {
      return undefined;
    }
    return this.assignedFilter;
  }

  teammateAdmins(): SupportAdminOption[] {
    const currentAdminUserId = this.currentUserService.shoutUser?.firebaseUid ?? '';
    if (!currentAdminUserId) {
      return this.admins;
    }
    return this.admins.filter((admin) => admin.userId !== currentAdminUserId);
  }

  loadStats(): void {
    this.statsLoading.set(true);
    this.subscriptionList.add(
      this.supportService.getAdminTicketStats(this.statsWindow).subscribe({
        next: (stats) => {
          this.stats.set(stats);
          this.statsLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.stats.set(null);
          this.statsLoading.set(false);
          this.cdr.markForCheck();
        },
      }),
    );
  }

  onStatsWindowChange(window: SupportStatsWindow): void {
    if (this.statsWindow === window) {
      return;
    }
    this.statsWindow = window;
    this.loadStats();
  }

  statsWindowButtonClass(window: SupportStatsWindow): string {
    return this.statsWindow === window
      ? 'rounded px-3 py-1.5 font-medium bg-indigo-600 text-white'
      : 'rounded px-3 py-1.5 font-medium text-secondary hover:bg-gray-50 dark:hover:bg-zinc-800';
  }

  formatReopenRate(rate?: number | null): string {
    if (rate == null || !Number.isFinite(rate)) {
      return '—';
    }
    return `${Math.round(rate * 100)}%`;
  }

  /** Tickets predating the outbound feature have no `initiatedBy`, and were all user-raised. */
  ticketDirection(ticket: SupportTicketSummary): string {
    return ticket.initiatedBy === 'admin' ? 'Outbound' : 'Inbound';
  }

  ticketDirectionClass(ticket: SupportTicketSummary): string {
    return ticket.initiatedBy === 'admin'
      ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
      : 'bg-gray-100 text-gray-600 dark:bg-zinc-700 dark:text-zinc-300';
  }

  /** Clicking the active tile clears the filter, so the tiles never trap you in a subset. */
  toggleDirectionFilter(direction: SupportTicketDirection): void {
    this.directionFilter.update((current) => (current === direction ? null : direction));
    this.loadTickets(true);
  }

  directionTileClass(direction: SupportTicketDirection): string {
    const base =
      'rounded-lg border px-4 py-3 text-left transition-colors hover:border-indigo-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600';
    return this.directionFilter() === direction
      ? `${base} border-indigo-600 bg-indigo-50 dark:border-indigo-400 dark:bg-indigo-950`
      : `${base} border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900`;
  }

  openTicket(ticketId: string): void {
    this.router.navigate(['/support-admin', ticketId], {
      state: { ticketIds: this.tickets().map((ticket) => ticket.id) },
    });
  }

  openOutbound(): void {
    this.showOutbound.set(true);

    if (!this.templatesLoadedOnce) {
      this.subscriptionList.add(
        this.supportService.getSupportSettings().subscribe({
          next: (settings) => {
            this.templateMessages = settings.templateMessages ?? [];
            this.templatesLoadedOnce = true;
            this.cdr.markForCheck();
          },
        }),
      );
    }
  }

  closeOutbound(): void {
    this.showOutbound.set(false);
  }

  onOutboundCreated(ticket: SupportTicketDetail): void {
    this.showOutbound.set(false);
    this.loadTickets();
    this.router.navigate(['/support-admin', ticket.id]);
  }

  openSettings(): void {
    this.showSettings.set(true);
    this.settingsError.set('');
    this.settingsLoading.set(true);
    this.autoAssignByCategory = this.createEmptyAutoAssignByCategory();
    this.resetAutomationSettings();

    this.subscriptionList.add(
      this.supportService.listAssignableAdmins().subscribe({
        next: (admins) => {
          this.admins = admins;
          this.cdr.markForCheck();
        },
      }),
    );

    this.subscriptionList.add(
      this.supportService.getSupportSettings().subscribe({
        next: (settings) => {
          this.autoAssignByCategory = {
            ...this.createEmptyAutoAssignByCategory(),
            ...settings.autoAssignByCategory,
          };
          this.applyAutomationSettings(settings);
          this.settingsLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.settingsError.set('Could not load settings.');
          this.settingsLoading.set(false);
          this.cdr.markForCheck();
        },
      }),
    );
  }

  closeSettings(): void {
    this.showSettings.set(false);
    this.settingsError.set('');
  }

  saveSettings(): void {
    this.settingsSaving.set(true);
    this.settingsError.set('');

    this.subscriptionList.add(
      this.supportService
        .updateSupportSettings({
          autoAssignByCategory: this.autoAssignByCategory,
          autoCloseEnabled: this.autoCloseEnabled,
          autoCloseAfterDays: this.autoCloseAfterDays,
          notifyUserOnAdminClose: this.notifyUserOnAdminClose,
          notifyUserOnAutoClose: this.notifyUserOnAutoClose,
          adminReplyNotifyDelayMinutes: this.adminReplyNotifyDelayMinutes,
          skipDelayForFirstResponse: this.skipDelayForFirstResponse,
        })
        .subscribe({
          next: (settings) => {
            this.autoAssignByCategory = {
              ...this.createEmptyAutoAssignByCategory(),
              ...settings.autoAssignByCategory,
            };
            this.applyAutomationSettings(settings);
            this.settingsSaving.set(false);
            this.closeSettings();
            this.cdr.markForCheck();
          },
          error: () => {
            this.settingsError.set('Could not save settings.');
            this.settingsSaving.set(false);
            this.cdr.markForCheck();
          },
        }),
    );
  }

  openTemplates(): void {
    this.showTemplates.set(true);
    this.templatesError.set('');
    this.templatesLoading.set(true);
    this.templateMessages = [];

    this.subscriptionList.add(
      this.supportService.getSupportSettings().subscribe({
        next: (settings) => {
          this.templateMessages = (settings.templateMessages ?? []).map((template) => ({
            ...template,
          }));
          this.templatesLoadedOnce = true;
          this.templatesLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.templatesError.set('Could not load templates.');
          this.templatesLoading.set(false);
          this.cdr.markForCheck();
        },
      }),
    );
  }

  closeTemplates(): void {
    this.showTemplates.set(false);
    this.templatesError.set('');
  }

  saveTemplates(): void {
    const incomplete = this.templateMessages.find(
      (template) => !template.label.trim() || !template.body.trim(),
    );
    if (incomplete) {
      this.templatesError.set('Each template needs a label and message before saving.');
      return;
    }

    const payload = this.normalizedTemplateMessages();
    if (!payload.length && this.templateMessages.length > 0) {
      this.templatesError.set('Add at least one complete template before saving.');
      return;
    }

    this.templatesSaving.set(true);
    this.templatesError.set('');

    this.subscriptionList.add(
      this.supportService
        .updateSupportSettings({
          templateMessages: payload,
        })
        .subscribe({
          next: (settings) => {
            this.templateMessages = settings.templateMessages ?? [];
            this.templatesLoadedOnce = true;
            this.templatesSaving.set(false);
            this.closeTemplates();
            this.cdr.markForCheck();
          },
          error: () => {
            this.templatesError.set('Could not save templates.');
            this.templatesSaving.set(false);
            this.cdr.markForCheck();
          },
        }),
    );
  }

  adminLabel(admin: SupportAdminOption): string {
    const name = `${admin.firstName} ${admin.lastName}`.trim();
    return name || admin.email;
  }

  formatAssignedAdminName(admin?: SupportUserSummary | null): string {
    if (!admin) {
      return 'Unassigned';
    }

    if (admin.firstName) {
      const lastInitial = admin.lastName?.charAt(0);
      return lastInitial ? `${admin.firstName} ${lastInitial}` : admin.firstName;
    }

    const parts = admin.fullName.trim().split(/\s+/);
    if (parts.length <= 1) {
      return parts[0] || 'Unassigned';
    }

    return `${parts[0]} ${parts[parts.length - 1].charAt(0)}`;
  }

  formatAlertPreferences(preferences?: SupportAlertPreferences): string {
    if (!preferences) {
      return '—';
    }

    const labels: string[] = [];
    if (preferences.sms) labels.push('SMS');
    if (preferences.whatsapp) labels.push('WhatsApp');
    if (preferences.email) labels.push('Email');
    if (preferences.push) labels.push('Push');
    return labels.length ? labels.join(', ') : '—';
  }

  formatUserType(userType?: SupportUserType | null): string {
    switch (userType) {
      case 'creator':
        return 'Creator';
      case 'brand':
        return 'Brand';
      case 'agency':
        return 'Agency';
      default:
        return '—';
    }
  }

  lastReplyLabel(authorType?: SupportAuthorType | null): string {
    if (authorType === 'admin') {
      return 'Support';
    }
    if (authorType === 'user') {
      return 'User';
    }
    return '—';
  }

  lastReplyClass(authorType?: SupportAuthorType | null): string {
    if (authorType === 'admin') {
      return 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
    }
    if (authorType === 'user') {
      return 'bg-amber-50 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
    }
    return 'bg-gray-100 text-gray-600 dark:bg-zinc-700 dark:text-zinc-300';
  }

  statusClass(status: SupportTicketStatus): string {
    return status === 'open'
      ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
      : 'bg-gray-100 text-gray-600 dark:bg-zinc-700 dark:text-zinc-300';
  }

  addTemplateMessage(): void {
    this.templateMessages = [...this.templateMessages, createSupportTemplateMessage()];
  }

  removeTemplateMessage(index: number): void {
    this.templateMessages = this.templateMessages.filter((_, i) => i !== index);
  }

  insertFirstNameToken(template: SupportTemplateMessage, textarea: HTMLTextAreaElement): void {
    const { body, cursor } = insertSupportTemplateToken(
      template.body,
      SUPPORT_FIRSTNAME_TOKEN,
      textarea.selectionStart,
      textarea.selectionEnd,
    );
    template.body = body;

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(cursor, cursor);
    });
  }

  preventDefault(event: Event): void {
    event.preventDefault();
  }

  private normalizedTemplateMessages(): SupportTemplateMessage[] {
    return this.templateMessages
      .map((template) => ({
        id: template.id,
        label: template.label.trim(),
        body: template.body.trim(),
      }))
      .filter((template) => template.label && template.body);
  }

  private createEmptyAutoAssignByCategory(): SupportAutoAssignByCategory {
    return {
      bug_report: null,
      feature_request: null,
      support_question: null,
      blank_issue: null,
    };
  }

  private resetAutomationSettings(): void {
    this.autoCloseEnabled = true;
    this.autoCloseAfterDays = 7;
    this.notifyUserOnAdminClose = true;
    this.notifyUserOnAutoClose = true;
    this.adminReplyNotifyDelayMinutes = 2;
    this.skipDelayForFirstResponse = true;
  }

  private applyAutomationSettings(settings: {
    autoCloseEnabled?: boolean;
    autoCloseAfterDays?: number;
    notifyUserOnAdminClose?: boolean;
    notifyUserOnAutoClose?: boolean;
    adminReplyNotifyDelayMinutes?: number;
    skipDelayForFirstResponse?: boolean;
  }): void {
    this.autoCloseEnabled = settings.autoCloseEnabled !== false;
    this.autoCloseAfterDays =
      typeof settings.autoCloseAfterDays === 'number' ? settings.autoCloseAfterDays : 7;
    this.notifyUserOnAdminClose = settings.notifyUserOnAdminClose !== false;
    this.notifyUserOnAutoClose = settings.notifyUserOnAutoClose !== false;
    this.adminReplyNotifyDelayMinutes =
      typeof settings.adminReplyNotifyDelayMinutes === 'number'
        ? settings.adminReplyNotifyDelayMinutes
        : 2;
    this.skipDelayForFirstResponse = settings.skipDelayForFirstResponse !== false;
  }
}
